import React from 'react';

const VIDEO_EMBED_URL = 'https://www.youtube.com/embed/zgTyl2eVupM';
const CSM_URL =
  'https://forms.clickup.com/4662065/f/4e8th-178237/5UQO886BFALC66SLGQ';

// Gradientes / fuentes tomados del Figma (node 11120:371)
const TITLE_GRADIENT =
  'linear-gradient(90deg, #0061fe 2%, #00296b 50%, #ee4a79 82%)';
const BLUE_TEXT_GRADIENT = 'linear-gradient(90deg, #0061fe 0%, #00aaff 100%)';
const URBANIST = "'Urbanist', 'DM Sans', sans-serif";
const DMSANS = "'DM Sans', sans-serif";

const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
    <path
      d="M6 6L14 14M14 6L6 14"
      stroke="#94A3B8"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

// Ícono de calendario (Lucide "calendar-days") con stroke en gradiente azul (Figma)
const CalendarIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <defs>
      <linearGradient
        id="advCalendarGrad"
        x1="0"
        y1="0"
        x2="24"
        y2="0"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#0061fe" />
        <stop offset="1" stopColor="#00aaff" />
      </linearGradient>
    </defs>
    <g
      stroke="url(#advCalendarGrad)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
      <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" />
    </g>
  </svg>
);

// Íconos de Lucide (megaphone, trophy, image, users)
const lucideProps = (color: string) => ({
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: color,
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const
});

const CampaignsIcon = ({ color }: { color: string }) => (
  <svg {...lucideProps(color)}>
    <path d="m3 11 18-5v12L3 14v-3z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </svg>
);

const RetosIcon = ({ color }: { color: string }) => (
  <svg {...lucideProps(color)}>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const ContentIcon = ({ color }: { color: string }) => (
  <svg {...lucideProps(color)}>
    <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
  </svg>
);

const GroupsIcon = ({ color }: { color: string }) => (
  <svg {...lucideProps(color)}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const HeadsetIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 13v-1a8 8 0 0116 0v1m0 0v3a2 2 0 01-2 2h-1v-6h1a2 2 0 012 2zM4 13a2 2 0 012-2h1v6H6a2 2 0 01-2-2v-2zM18 18v1a3 3 0 01-3 3h-3"
      stroke="#0061fe"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface Feature {
  icon: (p: { color: string }) => JSX.Element;
  iconColor: string;
  iconBg: string;
  title: string;
  text: string;
}

const FEATURES: Feature[] = [
  {
    icon: CampaignsIcon,
    iconColor: '#0061fe',
    iconBg: '#e6efff',
    title: 'Campañas',
    text:
      'Crea y gestiona campañas desde una interfaz rediseñada, más clara y rápida de usar. Adicional la IA te da sugerencias para tus próximas campañas.'
  },
  {
    icon: RetosIcon,
    iconColor: '#10b981',
    iconBg: '#e7f8f0',
    title: 'IA',
    text:
      'Configura las reglas de tu marca para que la IA revise automáticamente cada contenido y defina si aplica antes de aprobarlo y publicarlo.'
  },
  {
    icon: ContentIcon,
    iconColor: '#f97316',
    iconBg: '#fff0e6',
    title: 'Contenido & Posts',
    text:
      'Revisa, aprueba y gestiona todo el contenido generado por tus creadores en un solo lugar.'
  },
  {
    icon: GroupsIcon,
    iconColor: '#eab308',
    iconBg: '#fff8e1',
    title: 'Grupos & Recompensas',
    text:
      'Administra tus equipos y premia a los embajadores más activos desde una experiencia renovada.'
  }
];

export interface AnnouncementModalProps {
  open: boolean;
  onClose: () => void;
  onContactCsm?: () => void;
}

export const AnnouncementModal: React.FC<AnnouncementModalProps> = ({
  open,
  onClose,
  onContactCsm
}) => {
  if (!open) return null;

  const openLink = (url: string) => window.open(url, '_blank', 'noopener');

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 99999,
          animation: 'announceFadeIn 0.2s ease'
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 100000,
          width: 'calc(100% - 32px)',
          maxWidth: 1080,
          background: '#fff',
          borderRadius: 24,
          boxShadow: '0 24px 70px rgba(0,0,0,0.2), 0 1px 3px rgba(0,0,0,0.08)',
          fontFamily: DMSANS,
          animation: 'announceSlideUp 0.25s ease'
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Cerrar"
          style={{
            position: 'absolute',
            top: 18,
            right: 18,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 6,
            borderRadius: 8,
            zIndex: 1,
            lineHeight: 0
          }}
        >
          <CloseIcon />
        </button>

        <div
          style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch' }}
        >
          {/* Left column ── 619/1337 ≈ 46% */}
          <div
            style={{
              flex: '1 1 460px',
              minWidth: 300,
              padding: '22px 26px',
              borderRadius: '24px 0 0 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              background:
                'radial-gradient(120% 90% at 100% 0%, rgba(247,172,193,0.32) 0%, rgba(255,255,255,0) 55%), radial-gradient(120% 90% at 0% 100%, rgba(176,206,255,0.32) 0%, rgba(255,255,255,0) 55%), #ffffff'
            }}
          >
            {/* Badge */}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                alignSelf: 'flex-start',
                padding: '6px 12px',
                borderRadius: 50,
                border: '1px solid rgba(0,97,254,0.55)',
                background:
                  'linear-gradient(90deg, #cfe0ff 0%, #eef4ff 24%, #fdeef3 80%, #fcd9e2 100%)',
                fontFamily: DMSANS,
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: '-0.32px'
              }}
            >
              <CalendarIcon />
              <span
                style={{
                  backgroundImage: BLUE_TEXT_GRADIENT,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                Nuevo lanzamiento
              </span>
            </span>

            {/* Title */}
            <h2
              style={{
                fontFamily: URBANIST,
                fontSize: 26,
                lineHeight: 1.1,
                letterSpacing: '-0.6px',
                fontWeight: 600,
                margin: 0,
                backgroundImage: TITLE_GRADIENT,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Advocates se renovó, conoce nuestra nueva versión
            </h2>

            {/* Subtitle */}
            <p
              style={{
                fontFamily: DMSANS,
                fontSize: 13.5,
                fontWeight: 400,
                color: '#3d3d3d',
                lineHeight: 1.35,
                letterSpacing: '-0.3px',
                margin: 0
              }}
            >
              Rediseñamos la plataforma desde cero. A partir de hoy podrás
              contactar a tu equipo de CSM y solicitar la activación de nuestra
              nueva versión.
            </p>

            {/* Video embebido de YouTube (CSP de QA ya permite www.youtube.com) */}
            <div
              style={{
                position: 'relative',
                flex: 1,
                borderRadius: 18,
                overflow: 'hidden',
                minHeight: 200,
                background: '#000',
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
              }}
            >
              <iframe
                src={`${VIDEO_EMBED_URL}?rel=0&origin=${encodeURIComponent(
                  window.location.origin
                )}`}
                title="Ver nuevo Advocates"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                // El sitio manda Referrer-Policy: no-referrer → YouTube da Error 153.
                // Este override envía solo el origin (no la ruta) a YouTube para validar el embed.
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>

          {/* Right column ── 718/1337 ≈ 54% */}
          <div
            style={{
              flex: '1 1 540px',
              minWidth: 300,
              padding: '22px 26px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12
            }}
          >
            <h3
              style={{
                fontFamily: DMSANS,
                fontSize: 16,
                fontWeight: 600,
                letterSpacing: '-0.4px',
                color: '#222222',
                margin: 0
              }}
            >
              Qué encuentras en la nueva versión
            </h3>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                gap: 12
              }}
            >
              {FEATURES.map(f => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.title}
                    style={{
                      border: '1px solid #f4f6f9',
                      borderRadius: 14,
                      padding: '11px 13px',
                      boxShadow: '0 4px 14px rgba(16,24,40,0.06)'
                    }}
                  >
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 11,
                        background: f.iconBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 8
                      }}
                    >
                      <Icon color={f.iconColor} />
                    </div>
                    <p
                      style={{
                        fontFamily: URBANIST,
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#3d3d3d',
                        margin: '0 0 3px'
                      }}
                    >
                      {f.title}
                    </p>
                    <p
                      style={{
                        fontFamily: DMSANS,
                        fontSize: 12,
                        fontWeight: 400,
                        color: '#7d7d7d',
                        lineHeight: 1.3,
                        margin: 0
                      }}
                    >
                      {f.text}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* CSM CTA */}
            <div
              style={{
                marginTop: 'auto',
                padding: '13px 16px',
                borderRadius: 14,
                background:
                  'linear-gradient(90deg, #e6efff 0%, #ffffff 51%, #ffe9f0 100%)'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 4
                }}
              >
                <HeadsetIcon />
                <p
                  style={{
                    fontFamily: URBANIST,
                    fontSize: 14,
                    fontWeight: 600,
                    margin: 0,
                    backgroundImage: BLUE_TEXT_GRADIENT,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  Sé de los primeros en actualizarte
                </p>
              </div>
              <p
                style={{
                  fontFamily: DMSANS,
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#00358c',
                  lineHeight: 1.35,
                  margin: '0 0 10px'
                }}
              >
                Inscríbete en la lista prioritaria y accede antes que nadie a la
                nueva versión de AdvPro.
              </p>
              <button
                onClick={() => {
                  openLink(CSM_URL);
                  onContactCsm?.();
                }}
                style={{
                  width: '100%',
                  padding: '9px 18px',
                  borderRadius: 10,
                  border: 'none',
                  background: '#ee4a79',
                  color: '#fff',
                  fontFamily: URBANIST,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(238,74,121,0.35)'
                }}
              >
                Inscríbete
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Urbanist:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes announceFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes announceSlideUp { from { opacity: 0; transform: translate(-50%, -46%) } to { opacity: 1; transform: translate(-50%, -50%) } }
      `}</style>
    </>
  );
};

export default AnnouncementModal;
